// @flow
import map from "lodash/map";
import orderBy from "lodash/orderBy";
import { FormBuilderContext } from "pages/Contexts/FormBuilderContext";
import * as React from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import type { Question } from "types/question";
import type { QuestionChoice } from "types/question-choice";
import Button from "ui/atoms/Button";
import Flex from "ui/atoms/Flex";
import FlexBox from "ui/atoms/FlexBox";
import Space from "ui/atoms/Space";
import Text from "ui/atoms/Text";

import cn from "@/utils/tailwind/cn";

import BuilderCheckboxEditPropsContentIntegrationValue from "./BuilderCheckboxEditPropsContentIntegrationValue";
import BuilderChoiceProp from "./BuilderChoiceProp";

type QuestionProps = {
  question_choices?: Array<QuestionChoice>,
  default_value?: ?string,
};

type Props = {
  questionId: number,
  onUpdateQuestionProps: (props: QuestionProps) => void,
  mayRequestDetails?: boolean,
  assignValues?: boolean,
  includeIntegrationValues?: boolean,
  question: Question,
};

function BuilderCheckboxEditPropsContent({
  questionId,
  onUpdateQuestionProps,
  mayRequestDetails,
  assignValues,
  includeIntegrationValues = false,
  question,
}: Props): React.Node {
  const { onReorderQuestionProp, addQuestionChoice, removeQuestionChoice } =
    React.useContext(FormBuilderContext);

  const questionChoices = question.question_choices;

  const onUpdateOptionValue = (optionId: string, newValue: string) => {
    onUpdateQuestionProps({
      question_choices: map(questionChoices, (choice) =>
        choice.id !== optionId
          ? choice
          : {
              ...choice,
              text_value: newValue,
              integration_value:
                choice.integration_value === choice.text_value
                  ? newValue
                  : choice.integration_value,
            },
      ),
    });
  };

  const onUpdateOptionNumberValue = (optionId: string, value: string) => {
    onUpdateQuestionProps({
      question_choices: map(questionChoices, (choice) =>
        choice.id !== optionId
          ? choice
          : {
              ...choice,
              number_value: value || null,
            },
      ),
    });
  };

  function getNewDefaultValue(optionId: string) {
    const currentDefaultValues = (question.default_value || "")
      .split(",")
      .filter(Boolean);

    if (currentDefaultValues.includes(optionId)) {
      return currentDefaultValues.filter((item) => item !== optionId).join(",");
    }

    return question.question_type.question_type === "checkbox"
      ? [...currentDefaultValues, optionId].join(",")
      : optionId;
  }

  const onUpdateDefaultValues = (optionId: string) => {
    const value = getNewDefaultValue(optionId);

    onUpdateQuestionProps({
      default_value: value || null,
    });
  };

  const onAddOption = React.useCallback(() => {
    addQuestionChoice(questionId);
  }, [addQuestionChoice, questionId]);

  const onRemoveOption = React.useCallback(
    (choiceId) => {
      removeQuestionChoice(questionId, choiceId);
    },
    [removeQuestionChoice, questionId],
  );

  const onToggleRequestDetails = React.useCallback(
    (optionId, newValue) => {
      const question_choices = map(questionChoices, (choice) =>
        choice.id !== optionId
          ? choice
          : {
              ...choice,
              request_details: newValue,
            },
      );
      onUpdateQuestionProps({
        question_choices,
      });
    },
    [questionChoices, onUpdateQuestionProps],
  );

  const onReorder = React.useCallback(
    (result) => {
      if (!result.destination) {
        return;
      }

      if (result.destination.index === result.source.index) {
        return;
      }

      onReorderQuestionProp(
        questionId,
        result.type,
        result.source.droppableId,
        result.source.index,
        result.destination.index,
      );
    },
    [onReorderQuestionProp, questionId],
  );

  return (
    <Space>
      <DragDropContext onDragEnd={onReorder}>
        <Droppable droppableId={`${questionId}`} type="question_choices">
          {(providedDrop) => (
            <div ref={providedDrop.innerRef} {...providedDrop.droppableProps}>
              <div className="w-full flex flex-col">
                {includeIntegrationValues && (
                  <Flex>
                    <FlexBox flex={1.6}>
                      <Text>Choices</Text>
                    </FlexBox>
                    <FlexBox>
                      <Text>Integration Value</Text>
                    </FlexBox>
                  </Flex>
                )}
                {orderBy(questionChoices, ["order"], ["asc"]).map(
                  (choice, index) => (
                    <Draggable
                      draggableId={String(choice.id)}
                      index={index}
                      key={choice.id}
                    >
                      {(providedDrag, snapshotDrag) => (
                        <div
                          ref={providedDrag.innerRef}
                          {...providedDrag.draggableProps}
                          {...providedDrag.dragHandleProps}
                          className={cn(
                            "bg-white !rounded-xl border border-white",
                            snapshotDrag.isDragging && "shadow",
                          )}
                        >
                          <Flex $horizontalSpacing="large" align="end">
                            <FlexBox flex={1.5}>
                              <BuilderChoiceProp
                                includeDefaultValues
                                assignValues={assignValues}
                                defaultValue={question.default_value}
                                id={choice.id}
                                index={index}
                                key={choice.id}
                                mayRequestDetails={mayRequestDetails}
                                numberValue={choice.number_value}
                                onRemove={onRemoveOption}
                                onToggleRequestDetails={onToggleRequestDetails}
                                onUpdateDefaultValues={onUpdateDefaultValues}
                                onUpdateNumberValue={onUpdateOptionNumberValue}
                                onUpdateValue={onUpdateOptionValue}
                                requestDetails={choice.request_details}
                                value={choice.text_value}
                              />
                            </FlexBox>
                            {includeIntegrationValues && (
                              <FlexBox>
                                <BuilderCheckboxEditPropsContentIntegrationValue
                                  choice={choice}
                                  onUpdateQuestionProps={onUpdateQuestionProps}
                                  questionChoices={questionChoices}
                                />
                              </FlexBox>
                            )}
                          </Flex>
                        </div>
                      )}
                    </Draggable>
                  ),
                )}
              </div>
              {providedDrop.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <Button $compact onClick={onAddOption} style={{ marginLeft: "1rem" }}>
        <Text $small>Add choice</Text>
      </Button>
    </Space>
  );
}

export default BuilderCheckboxEditPropsContent;
